import React from 'react';
import { useState, useMemo, useCallback } from 'react';
import { useLanguage } from '../../../../../contexts/LanguageContext';
import {
    useGetAvailableSapDocumentsQuery,
    useGetDealerInvoiceHistoryQuery,
    useGetPendingApprovalsForWarrantyTeamQuery,
    useGetAllAvailableSapDocumentsForWarrantyTeamQuery,
    useGetAllGTOrdersQuery
} from '../../../../../api/endpoints/dealerInvoiceEntryApi';
import DealerDropdown from '../../../../../components/common/DealerDropdownWithMulti';
import { formatDateForApi } from '../../../../../utils/dateUtils';
import { TAB_IDS } from '../constants/invoiceTabs';

const compareValues = (a, b, field) => {
    if (field.toLowerCase().includes('date')) return new Date(a) - new Date(b);
    if (typeof a === 'number' && typeof b === 'number') return a - b;
    return String(a).localeCompare(String(b), 'tr', { numeric: true, sensitivity: 'base' });
};

export const useDealerInvoiceList = (th, activeTabId, currentStatus, isWarrantyTeam, dealerType) => {
    const { getCurrentLanguageId } = useLanguage();
    const isSapTab = activeTabId === TAB_IDS.SAP_RECORDS;

    const [lazyParams, setLazyParams] = useState({
        first: 0,
        rows: 10,
        page: 0
    });

    const [sortState, setSortState] = useState({ sortField: null, sortOrder: null });

    const [apiFilters, setApiFilters] = useState({});

    const filterFields = useMemo(() => {
        const commonFilters = [
            { field: 'groupId', headerName: th('dealerInvoice.groupId'), type: 'text' }
        ];

        // Garanti ekibi için Bayi Adı filtresi (tüm sekmelerde)
        const dealerFilter = isWarrantyTeam
            ? [{
                field: 'dealerCode',
                headerName: th('warrantyTeam.dealerName') || 'Bayi Adı',
                type: 'custom',
                customComponent: ({ value, onChange }) => (
                    <DealerDropdown
                        value={value}
                        onChange={(selected) => {
                            if (selected?.dealerIds?.length > 0) {
                                onChange(selected.dealerIds[0]);
                            } else {
                                onChange(null);
                            }
                        }}
                        className="w-full"
                        multiple={false}
                    />
                )
            }]
            : [];

        if (isSapTab) {
            return [...commonFilters, ...dealerFilter];
        }

        return [
            ...commonFilters,
            ...dealerFilter,
            { field: 'invoiceNumber', headerName: th('dealerInvoice.invoiceNumber'), type: 'text' },
            { field: 'startDate', headerName: th('common.startDate'), type: 'date' },
            { field: 'endDate', headerName: th('common.endDate'), type: 'date' }
        ];
    }, [th, isSapTab, isWarrantyTeam]);

    const queryParams = useMemo(() => {
        const params = {
            pageNumber: lazyParams.page + 1,
            pageSize: lazyParams.rows,
            languageId: getCurrentLanguageId(),
            ...apiFilters
        };

        if (dealerType) {
            params.dealerType = dealerType;
        }

        if (currentStatus !== null && currentStatus !== undefined) {
            params.status = currentStatus;
        }

        return params;
    }, [lazyParams, apiFilters, currentStatus, dealerType, getCurrentLanguageId]);

    // Garanti ekibinde Tüm GT Siparişleri sekmesi activeTab === 5
    const isAllGTTab = isWarrantyTeam && activeTabId === TAB_IDS.ALL_GT_ORDERS;

    const sapQuery = useGetAvailableSapDocumentsQuery(queryParams, {
        skip: isWarrantyTeam || !isSapTab
    });

    const warrantySapQuery = useGetAllAvailableSapDocumentsForWarrantyTeamQuery(queryParams, {
        skip: !isWarrantyTeam || !isSapTab
    });

    const dealerHistoryQuery = useGetDealerInvoiceHistoryQuery(queryParams, {
        skip: isWarrantyTeam || isSapTab
    });

    const warrantyQuery = useGetPendingApprovalsForWarrantyTeamQuery(queryParams, {
        skip: !isWarrantyTeam || isSapTab || isAllGTTab
    });

    // YENİ: Tüm GT Siparişleri query
    const allGTOrdersQuery = useGetAllGTOrdersQuery(queryParams, {
        skip: !isAllGTTab
    });

    const currentData = useMemo(() => {
        if (isWarrantyTeam) {
            if (isSapTab) return warrantySapQuery;
            if (isAllGTTab) return allGTOrdersQuery;
            return warrantyQuery;
        } else {
            if (isSapTab) return sapQuery;
            return dealerHistoryQuery;
        }
    }, [isWarrantyTeam, isSapTab, isAllGTTab, warrantySapQuery, warrantyQuery, sapQuery, dealerHistoryQuery, allGTOrdersQuery]);

    const items = useMemo(() => {
        const response = currentData.data;
        if (!response) return [];

        if (response.data && Array.isArray(response.data.items)) {
            return response.data.items;
        }
        if (Array.isArray(response.items)) {
            return response.items;
        }
        if (response.result && response.result.items) {
            return response.result.items;
        }

        return [];
    }, [currentData.data]);

    const sortedItems = useMemo(() => {
        const { sortField, sortOrder } = sortState;
        if (!sortField || !sortOrder) return items;

        return [...items].sort((a, b) => {
            const value1 = a[sortField];
            const value2 = b[sortField];
            if (value1 == null && value2 == null) return 0;
            if (value1 == null) return 1;
            if (value2 == null) return -1;
            return compareValues(value1, value2, sortField) * sortOrder;
        });
    }, [items, sortState]);

    const onSort = useCallback((event) => {
        setSortState({ sortField: event.sortField, sortOrder: event.sortOrder });
    }, []);

    const totalRecords = currentData.data?.data?.totalCount ||
        currentData.data?.totalCount ||
        currentData.data?.result?.totalCount ||
        0;

    const loading = currentData.isLoading || currentData.isFetching;

    const onPage = (event) => {
        setLazyParams(prev => ({ ...prev, ...event }));
    };

    const handleApplyFilters = useCallback((filters) => {
        const formattedFilters = {};

        Object.keys(filters).forEach(key => {
            const value = filters[key];

            if (value === null || value === undefined || value === '') {
                return;
            }

            if (key.includes('Date') && value) {
                // Yerel gün bileşenleriyle formatla; geçersizse ham değeri koru.
                formattedFilters[key] = formatDateForApi(value) || value;
            } else {
                formattedFilters[key] = value;
            }
        });
        setApiFilters(formattedFilters);
        setLazyParams(prev => ({ ...prev, first: 0, page: 0 }));
    }, []);

    const handleResetFilters = useCallback(() => {
        setApiFilters({});
        setLazyParams(prev => ({ ...prev, first: 0, page: 0 }));
    }, []);

    return {
        items: sortedItems,
        totalRecords,
        loading,
        lazyParams: { ...lazyParams, ...sortState },
        onPage,
        onSort,
        filterFields,
        handleApplyFilters,
        handleResetFilters,
        refetch: currentData.refetch,
        apiFilters
    };
};
